import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  User, UserCreateInput, UserUpdateInput,
  listUsers, createUser, updateUser, deactivateUser, activateUser, deleteUser,
} from '../services/usersApi';

interface UsersState {
  items: User[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchList: () => Promise<void>;
  create: (input: UserCreateInput) => Promise<User>;
  update: (id: number, input: UserUpdateInput) => Promise<User>;
  deactivate: (id: number) => Promise<User>;
  activate: (id: number) => Promise<User>;
  remove: (id: number) => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useUsersStore = create<UsersState>()(
  devtools(
    (set) => ({
      items: [],
      loading: false,
      saving: false,
      error: null,
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const res = await listUsers(1, 100);
          set({ items: res.users, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createUser(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateUser(id, input);
          set((s) => ({
            items: s.items.map((u) => (u.id === id ? updated : u)),
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      deactivate: async (id) => {
        const updated = await deactivateUser(id);
        set((s) => ({
          items: s.items.map((u) => (u.id === id ? { ...u, ...updated, is_active: false } : u)),
        }));
        return updated;
      },
      activate: async (id) => {
        const updated = await activateUser(id);
        set((s) => ({
          items: s.items.map((u) => (u.id === id ? { ...u, ...updated, is_active: true } : u)),
        }));
        return updated;
      },
      remove: async (id) => {
        await deleteUser(id);
        set((s) => ({ items: s.items.filter((u) => u.id !== id) }));
      },
    }),
    { name: 'users-store' }
  )
);
